import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { later, schedule } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { Service } from 'webapp-types';
import { ActivityLogEvent } from 'webapp/enums/activity-log';
import { ReportFragmentAction, type NewReportSlideFragmentLayout } from 'webapp/enums/enums';
import { AppEvent } from 'webapp/enums/events';
import { getOrderFromPosition } from 'webapp/utils/common';

import type Report from 'webapp/models-custom/report';
import type ReportSlide from 'webapp/models-custom/report-slide';
import type ReportSlideFragment from 'webapp/models-custom/report-slide-fragment';
import type ReportSlideRecord from 'webapp/models/report-slide';
import type ReportSlideFragmentRecord from 'webapp/models/report-slide-fragment';

import type { CreateFragmentResponseToGrid } from 'webapp/services/model-manager/report-slide-fragments';

export interface ReportFragmentActionAddData {
	fragmentType: NewReportSlideFragmentLayout;
	rowIndex: number;
	columnIndex: number | null;
}

export interface ReportFragmentActionRemoveData {
	rowIndex: number;
	columnIndex: number;
}

export const enum OrderProperty {
	ORDER = 'order',
	GLOBALORDER = 'globalOrder',
}

export function getPositionPropertyFromOrderProperty(orderProperty: OrderProperty) {
	switch (orderProperty) {
		case OrderProperty.ORDER:
			return 'position';
		case OrderProperty.GLOBALORDER:
			return 'globalPosition';
	}
}

export type OrdersData = { id: string; order: number }[];

export interface ReportFragmentActionOrderData {
	orders: OrdersData;
}

export interface ReportFragmentActionChangeTypeData {
	layout: NewReportSlideFragmentLayout;
}

export type ReportFragmentActionData =
	| ReportFragmentActionAddData
	| ReportFragmentActionRemoveData
	| ReportFragmentActionOrderData
	| ReportFragmentActionChangeTypeData;

export interface Args {
	model: Report;
	changeViewMode: (isPageless: boolean) => void;
	closeEditor: () => void;
}

type SavingQueueEntity = ReportSlide | ReportSlideFragment;
type SavingQueueEntityRecord = ReportSlideRecord | ReportSlideFragmentRecord;

export default abstract class ReportsEditorBaseComponent<T extends Args> extends Component<T> {
	@service('request') declare requestService: Service.Request;
	@service('popup') declare popupService: Service.Popup;
	@service('model-manager/report-slide-fragments')
	declare mmReportSlideFragmentsService: Service.ModelManager.ReportSlideFragments;
	@service('model-manager/activity-logs')
	declare mmActivityLogsService: Service.ModelManager.ActivityLogs;
	@service('event-manager') declare eventManagerService: Service.EventManager;
	@service('model-manager/data-selects')
	declare mmDataSelectsService: Service.ModelManager.DataSelects;
	@service('model-manager/report-slides')
	declare mmReportSlidesService: Service.ModelManager.ReportSlides;
	@service('model-manager/reports') declare mmReportsService: Service.ModelManager.Reports;
	@service('navigation') declare navigationService: Service.Navigation;

	@tracked isAnyAiSummaryBeingEdited = false;

	abstract get shownFragments(): ReportSlideFragment[];

	abstract orderProperty: OrderProperty;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		if (this.args.model.id && !this.args.model.isTemplate) {
			this.mmActivityLogsService.createAndSaveActivityLogItem(
				ActivityLogEvent.REPORT_VIEW,
				this.args.model.id,
			);
		}

		this.bindDataStoryElementOpenAdvanced();
		this.bindAiSummaryEditingStateChanges();
	}

	get isFragmentGridEditable() {
		return true;
	}

	get positionProperty() {
		return getPositionPropertyFromOrderProperty(this.orderProperty);
	}

	@action
	onFragmentChange(
		fragment: ReportSlideFragment,
		action: ReportFragmentAction,
		data?: ReportFragmentActionData,
	): Promise<unknown> {
		if (action === ReportFragmentAction.DELETE) {
			const removeData = data as ReportFragmentActionRemoveData;
			this.updateOrderOnFragmentRemove(fragment, removeData.rowIndex, removeData.columnIndex);

			return this.addToDeleteQueue(fragment);
		} else if (action === ReportFragmentAction.ORDER) {
			const orderData = data as ReportFragmentActionOrderData;
			return this.updateFragmentSortOrder(fragment, orderData.orders);
		} else if (action === ReportFragmentAction.CHANGE_TYPE) {
			const changeTypeData = data as ReportFragmentActionChangeTypeData;
			return this.changeFragmentType(fragment, changeTypeData.layout);
		} else {
			if (action === ReportFragmentAction.ADD) {
				const addData = data as ReportFragmentActionAddData;
				this.updateOrderOnFragmentAdd(fragment, addData.rowIndex, addData.columnIndex);
			}
			return this.addToSaveQueue(fragment);
		}
	}

	@action
	changeFragmentType(fragment: ReportSlideFragment, type: NewReportSlideFragmentLayout) {
		return this.mmReportSlideFragmentsService
			.addFragmentFromLayout(type, fragment.slide!, fragment)
			.then(({ fragment, afterFragmentCreateFn }: CreateFragmentResponseToGrid) => {
				return this.addToSaveQueue(fragment).then(() => {
					if (afterFragmentCreateFn) {
						return afterFragmentCreateFn();
					} else {
						return Promise.resolve();
					}
				});
			})
			.catch(() => {
				// do nothing, user probably closed popup
			});
	}

	private updateOrderOnFragmentAdd(
		fragment: ReportSlideFragment,
		rowIndex: number,
		columnIndex: number | null,
	): void {
		const isInsertNewRow = columnIndex === null;

		// only fragments on this slide are affected
		let affectedFragments = this.shownFragments.filter((f) => {
			return f.reportSlideId === fragment.reportSlideId;
		});

		let orderIncrease = 0;
		if (isInsertNewRow) {
			// all fragments after the new row move down a row
			const affectedOrderStart = getOrderFromPosition(rowIndex, 0);
			affectedFragments = affectedFragments.filter((f) => {
				return f[this.orderProperty] > affectedOrderStart;
			});
			orderIncrease = 10;
		} else {
			// all fragments in this row move a column to the right
			affectedFragments = affectedFragments.filter((f) => {
				const position = f[this.positionProperty];
				return position.row === rowIndex && position.column >= columnIndex;
			});
			orderIncrease = 1;
		}

		const reordersData = affectedFragments.map((f) => {
			return {
				id: f.id!,
				order: f[this.orderProperty] + orderIncrease,
			};
		});

		if (reordersData.length) {
			void this.updateFragmentSortOrder(fragment, reordersData);
		}

		fragment[this.orderProperty] = getOrderFromPosition(rowIndex, columnIndex ?? 1);
	}

	private updateOrderOnFragmentRemove(
		fragment: ReportSlideFragment,
		rowIndex: number,
		columnIndex: number,
	): void {
		// only fragments on this slide are affected
		let affectedFragments = this.shownFragments.filter((f) => {
			return f.reportSlideId === fragment.reportSlideId;
		});

		// Get fragments in the same row with higher column index
		affectedFragments = affectedFragments.filter((f) => {
			const position = f[this.positionProperty];
			return position.row === rowIndex && position.column > columnIndex;
		});

		// Decrease order by 1 for fragments to the right in the same row
		const reordersData = affectedFragments.map((f) => {
			return {
				id: f.id!,
				order: f[this.orderProperty] - 1,
			};
		});

		if (reordersData.length) {
			void this.updateFragmentSortOrder(fragment, reordersData);
		}
	}

	protected updateFragmentSortOrder(_fragment: ReportSlideFragment, ordersData: OrdersData) {
		let localOrdersData: OrdersData = [];
		if (this.orderProperty === OrderProperty.ORDER) {
			// all good
			localOrdersData = ordersData;
		} else {
			// transform from global
			this.shownFragments.forEach((fragment) => {
				const fragmentId = fragment.id!,
					globalOrder = ordersData.find((item) => item.id === fragmentId)?.order;

				if (fragment && globalOrder) {
					// translate global orders into local for save to BE
					const order = fragment.slide!.getOrderFromGlobalOrder(globalOrder);
					localOrdersData.push({ id: fragmentId, order });
				}
			});
		}

		// update models manually
		this.shownFragments.forEach((fragment) => {
			const order = localOrdersData.find((item) => item.id === fragment.id!)?.order;

			if (fragment && order) {
				fragment.directPropUpdate({ order });
			}
		});

		// save the moved items
		return this.requestService
			.api('PUT', `/reports/${this.args.model.id}/report-slide-fragments-order`, {
				reportSlideFragments: localOrdersData,
			})
			.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
	}

	// ------ Open/close block settings logic ------

	@tracked blockFragmentToEdit: ReportSlideFragment | null = null;

	get isBlockSettingsOpen() {
		const block = this.blockFragmentToEdit?.block;
		if (!block || this.blockFragmentToEdit?.model.isDeleted || this.isAnyAiSummaryBeingEdited) {
			return false;
		}

		return true;
	}

	get blockToEdit() {
		return this.blockFragmentToEdit?.block;
	}

	@action
	changeBlockFragmentToEdit(fragment: ReportSlideFragment | null) {
		const isOpen = !!this.isBlockSettingsOpen,
			willBeOpen = !!fragment;

		this.blockFragmentToEdit = fragment;

		if (isOpen !== willBeOpen) {
			schedule('afterRender', () => {
				$(window).trigger('resize.datawallcenter');
				later(() => {
					this.popupService.repositionAllPopups();
				}, 700);
			});
		}
	}

	private bindDataStoryElementOpenAdvanced() {
		this.eventManagerService.on(
			AppEvent.DATA_STORY_ELEMENT_OPEN_ADVANCED,
			this,
			(eventData) => {
				const block = eventData.block,
					fragment = this.shownFragments.find((f) => f.blockId === block.numericId);
				if (!fragment) {
					return;
				}
				const dataSelect = eventData.dataSelect,
					blockSetup = block.blockSetup;

				// only open advanced if we have this block already in sidebar
				if (this.blockFragmentToEdit !== fragment) {
					this.changeBlockFragmentToEdit(fragment);
				} else {
					this.mmDataSelectsService.openAdvancedPopup({
						dataSelect,
						blockSetup,
						sourceForTracking: 'story-element',
					});
				}
			},
		);
	}

	private bindAiSummaryEditingStateChanges() {
		this.eventManagerService.on(
			AppEvent.AI_SUMMARY_EDITING_STATE_CHANGED,
			this,
			(eventData) => {
				this.isAnyAiSummaryBeingEdited = eventData.isEditing;
			},
		);
	}

	override willDestroy() {
		super.willDestroy();
		this.eventManagerService.off(AppEvent.DATA_STORY_ELEMENT_OPEN_ADVANCED, this);
		this.eventManagerService.off(AppEvent.AI_SUMMARY_EDITING_STATE_CHANGED, this);
	}

	// ------ SAVING ONE ENTITY AT A TIME LOGIC ------

	private savePromises = new WeakMap<SavingQueueEntityRecord, Promise<unknown>>();

	protected addToSaveQueue(model: SavingQueueEntity) {
		// parallel saving of diff fragments, but only one request per fragment must be active

		if (!model.isNew && !model.isDirty) {
			return Promise.resolve();
		}

		// apply changes now, and force save later
		model.applyBufferedChanges();

		const record = model.model;

		let existingPromise = this.savePromises.get(record);
		if (existingPromise) {
			existingPromise = existingPromise.then(async () => {
				return model.saveAndCatch(true);
			});
			this.savePromises.set(record, existingPromise);
			return existingPromise;
		} else {
			const promise = model.saveAndCatch(true);
			this.savePromises.set(record, promise);
			return promise;
		}
	}

	protected addToDeleteQueue(model: SavingQueueEntity): Promise<unknown> {
		// no queue, delete immediately
		return model.deleteAndCatch();
	}
}
